import type { QCSeverity } from '@ohear/shared'
/**
 * TanStack Query hooks for take upload + history.
 *
 * Upload flow (PRD §10):
 *   1. POST /api/v1/actor/cues/{cueId}/takes/presign  → { take_id, presigned_put_url }
 *   2. PUT presigned_put_url with the WAV body (handled by @ohear/audio UploadQueue)
 *   3. POST /api/v1/actor/takes/{take_id}/finalize    → server runs QC + STT
 *
 * The finalize response carries the server QC verdict so the UI can advance to
 * REVIEW (or back to THREE_POP if QC failed).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

/** One previous take, used by TakeHistory. */
export interface TakeHistoryEntry {
  take_id: string
  cue_id: string
  take_number: number
  version_number: number
  recorded_at: string
  duration_sec: number
  peak_dbfs: number | null
  /** Server preview URL (FLAC, presigned, ~10 minute TTL). */
  preview_url: string | null
  /** Server-side QC outcome. */
  qc_summary: QCSeverity | null
  /** Whether the supervisor has marked this as the best take. */
  is_best_take: boolean
}

const cueTakesKey = (cueId: string) => ['actor', 'cue', cueId, 'takes'] as const

/** Take history for one cue. */
export function useCueTakes(cueId: string | null) {
  return useQuery({
    queryKey: cueTakesKey(cueId ?? '__none'),
    queryFn: () => api<{ takes: TakeHistoryEntry[] }>(`/api/v1/actor/cues/${cueId}/takes`),
    enabled: Boolean(cueId),
  })
}

export interface PresignTakeResponse {
  take_id: string
  presigned_put_url: string
  expires_at: string
}

/** Mint a presigned PUT for a new take. The take_id round-trips with the WAV. */
export function usePresignTake() {
  return useMutation({
    mutationFn: async (input: {
      cueId: string
      takeNumber: number
      versionNumber: number
      durationSec: number
      sha256: string
    }) =>
      api<PresignTakeResponse>(`/api/v1/actor/cues/${input.cueId}/takes/presign`, {
        method: 'POST',
        body: JSON.stringify({
          take_number: input.takeNumber,
          version_number: input.versionNumber,
          duration_sec: input.durationSec,
          sha256: input.sha256,
        }),
      }),
  })
}

export interface FinalizeTakeResponse {
  take_id: string
  qc_summary: QCSeverity
  /** Server QC checks (one row per check). */
  qc_results: Array<{
    check_id: string
    severity: QCSeverity
    message: string | null
  }>
  /** STT transcript (server-side WhisperX). */
  stt_transcript: string | null
  /** Match confidence between STT and the cue's expected line. */
  stt_match_confidence: number | null
}

/**
 * Finalize a take after the WAV is uploaded — triggers server QC + STT.
 * Invalidates the cue-takes query on success so TakeHistory refreshes.
 */
export function useFinalizeTake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { takeId: string; cueId: string }) =>
      api<FinalizeTakeResponse>(`/api/v1/actor/takes/${input.takeId}/finalize`, {
        method: 'POST',
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: cueTakesKey(vars.cueId) })
    },
  })
}

/** Mark a take as the actor-preferred best take (overrides previous selection). */
export function useMarkBestTake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { takeId: string; cueId: string }) =>
      api<{ ok: true }>(`/api/v1/actor/takes/${input.takeId}/best`, { method: 'POST' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: cueTakesKey(vars.cueId) })
    },
  })
}
